<script setup lang="ts">
import { computed, onMounted } from 'vue'

import { filterStayListColumns } from '@/stay-list/stay-list-table/helpers/columnFilters'
import StayListLine from '@/stay-list/stay-list-table/mco/McoStayListLine.vue'
import StayListColumns from '@/stay-list/stay-list-table/mco/StayListColumns'
import { ListMcoStay } from '@/stay-list/stay-list-table/mco/types'
import StayListTable from '@/stay-list/stay-list-table/StayListTable.vue'
import { useStore } from '@/store'
import { AutomationModeEnum } from '@/store/modules/automation/types'
import { ModeEnum, PmsiTypeEnum } from '@/store/modules/settings/types'

const store = useStore()

onMounted(() => {
  store.commit('setStayListParam', { type: 'mode', value: ModeEnum.AUTOMATION })
})

const stayList = computed(() => store.state.automation.automationStayList.items)
const automationMode = computed(() => store.state.automation.automationStayListParams.automationMode)
const automationGroup = computed(() => store.state.automation.editedAutomationGroup)
const highlightedStayMissingAutomationCriteriaDisplayed = computed(
  () => [AutomationModeEnum.WAITING, AutomationModeEnum.EXPIRED].includes(automationMode.value) && automationGroup.value
)

const order = computed(() => store.state.automation.automationStayListParams.order)
const reverseOrder = computed(() => store.state.automation.automationStayListParams.reverseOrder)

const automationStayListRequestFetching = computed(() => store.state.automation.automationStayListRequest.fetching)

function getModeColumns() {
  return filterStayListColumns(
    StayListColumns,
    (columnKey) => StayListColumns[columnKey].mode.includes(ModeEnum.AUTOMATION)
    && StayListColumns[columnKey].automationType?.includes(automationGroup.value.type)
  )
}

function updateOrder(toBeOrderedColumn: string) {
  store.dispatch('updateAutomStayListOrder', toBeOrderedColumn)
}

const emit = defineEmits<{
  (e: 'display-stay-missing-criteria', stay: ListMcoStay)
}>()
function displayStayMissingCriteria(stay: ListMcoStay) {
  if (!highlightedStayMissingAutomationCriteriaDisplayed.value) {
    return
  }
  emit('display-stay-missing-criteria', stay)
}
</script>

<template>
  <stay-list-table
    :is-loading="automationStayListRequestFetching"
    :available-columns="getModeColumns()"
    :pmsi-type="PmsiTypeEnum.MCO"
    :mode="ModeEnum.AUTOMATION"
    :automation-type="automationGroup.type"
    :stay-list="stayList"
    :order="order"
    :reverse-order="reverseOrder"
    @stay-line-highlight="displayStayMissingCriteria"
    @order-update="updateOrder"
  >
    <template #line="lineProps">
      <stay-list-line
        :stay="lineProps.stay"
        :columns="lineProps.columns"
        :mode="ModeEnum.AUTOMATION"
        :automation-type="automationGroup.type"
        :selected="lineProps.selected"
        :allow-selection="lineProps.canSelectStay"
        @toggle-selected-stay="lineProps.toggleSelectedStay"
        @mouseover="lineProps.onStayLineHighlight(lineProps.stay)"
      />
    </template>
  </stay-list-table>
</template>
